import React, { useRef, useMemo, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Text, Line } from '@react-three/drei';
import * as THREE from 'three';

interface DataItem {
  label: string;
  value: number;
  color: string;
}

interface Simple3DPieProps {
  data: DataItem[];
  innerRadius?: number;
  outerRadius?: number;
  thickness?: number;
  showPercent?: boolean;
  onSliceClick?: (label: string) => void;
}

const PieSlice = ({ 
  startAngle, 
  endAngle, 
  innerRadius, 
  outerRadius, 
  thickness, 
  color, 
  label, 
  percent,
  value,
  labelX,
  labelY,
  onHover,
  onLeave,
  onSliceClick
}: {
  startAngle: number;
  endAngle: number;
  innerRadius: number;
  outerRadius: number;
  thickness: number;
  color: string;
  label: string;
  percent: number;
  value: number;
  labelX: number;
  labelY: number;
  onHover: (data: { label: string; value: number; percent: number }) => void;
  onLeave: () => void;
  onSliceClick?: (label: string) => void;
}) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);
  
  const geometry = useMemo(() => {
    const shape = new THREE.Shape();
    const segments = 64; // More segments for smoother curves
    
    // Create the pie slice shape
    const angleStep = (endAngle - startAngle) / segments;
    
    // Start from center if innerRadius is 0, otherwise from inner radius
    if (innerRadius === 0) {
      shape.moveTo(0, 0);
      // Draw to start of outer arc
      shape.lineTo(
        outerRadius * Math.cos(startAngle),
        outerRadius * Math.sin(startAngle)
      );
    } else {
      shape.moveTo(
        innerRadius * Math.cos(startAngle),
        innerRadius * Math.sin(startAngle)
      );
    }
    
    // Draw outer arc
    for (let i = 0; i <= segments; i++) {
      const angle = startAngle + i * angleStep;
      shape.lineTo(
        outerRadius * Math.cos(angle),
        outerRadius * Math.sin(angle)
      );
    }
    
    // Draw inner arc (reverse direction) only if we have a donut
    if (innerRadius > 0) {
      for (let i = segments; i >= 0; i--) {
        const angle = startAngle + i * angleStep;
        shape.lineTo(
          innerRadius * Math.cos(angle),
          innerRadius * Math.sin(angle)
        );
      }
    }
    
    const extrudeSettings = {
      depth: thickness,
      bevelEnabled: true,
      bevelThickness: 2,
      bevelSize: 1,
      bevelSegments: 3,
    };
    
    return new THREE.ExtrudeGeometry(shape, extrudeSettings);
  }, [startAngle, endAngle, innerRadius, outerRadius, thickness]);

  // Calculate positions
  const midAngle = (startAngle + endAngle) / 2;
  
  // Line starts from outer edge of pie slice
  const edgeX = outerRadius * Math.cos(midAngle);
  const edgeY = outerRadius * Math.sin(midAngle);

  // Calculate hover position offset
  const hoverOffset = hovered ? 5 : 0;
  const positionX = hoverOffset * Math.cos(midAngle);
  const positionY = hoverOffset * Math.sin(midAngle);

  return (
    <group position={[positionX, positionY, 0]}>
      <mesh
        ref={meshRef}
        geometry={geometry}
        position={[0, 0, -thickness / 2]}
        onPointerEnter={(e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = "pointer";
          onHover({ label, value, percent });
        }}
        onPointerLeave={(e) => {
          e.stopPropagation();
          setHovered(false);
          document.body.style.cursor = "default";
          onLeave();
        }}
        onClick={(e) => {
          e.stopPropagation();
          onSliceClick?.(label);
        }}
      >
        <meshStandardMaterial 
          color={color} 
          transparent={true}
          opacity={hovered ? 1.0 : 0.95}
          metalness={0.1}
          roughness={0.3}
          emissive={hovered ? new THREE.Color(color).multiplyScalar(0.1) : new THREE.Color(0x000000)}
        />
      </mesh>
      
      {/* Edge wireframe overlay for better 3D definition */}
      <mesh
        geometry={geometry}
        position={[0, 0, -thickness / 2]}
      >
        <meshBasicMaterial 
          color={new THREE.Color(color).multiplyScalar(0.7)} 
          wireframe={true}
          transparent={true}
          opacity={0.2}
        />
      </mesh>
      
      {/* Connection line - L-shaped to avoid crossings */}
      <Line
        points={[
          [edgeX, edgeY, thickness / 2 + 2],
          [edgeX + (labelX - edgeX) * 0.6, edgeY, thickness / 2 + 2],
          [labelX, labelY, thickness / 2 + 2]
        ]}
        color="rgba(0, 0, 0, 0.4)"
        lineWidth={1}
      />
      
      {/* Label with both percentage and name inline */}
      <Text
        position={[labelX, labelY, thickness / 2 + 5]}
        fontSize={8}
        color="black"
        anchorX={labelX > 0 ? "left" : "right"}
        anchorY="middle"
      >
        {`${label} ${percent.toFixed(0)}%`}
      </Text>
    </group>
  );
};

const Simple3DPie: React.FC<Simple3DPieProps> = ({
  data,
  innerRadius = 0,
  outerRadius = 120,
  thickness = 18,
  showPercent = true,
  onSliceClick,
}) => {
  const [hoveredData, setHoveredData] = useState<{ label: string; value: number; percent: number } | null>(null);
  
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  let currentAngle = 0;
  const slices = data.map((item) => {
    const percent = (item.value / total) * 100;
    const angle = (item.value / total) * Math.PI * 2;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;
    
    return {
      ...item,
      startAngle,
      endAngle,
      percent,
    };
  });

  // Calculate optimized label positions to prevent overlaps
  const calculateLabelPositions = () => {
    const labelDistance = outerRadius + 30;
    const minVerticalSpacing = 20;
    
    const positions = slices.map((slice) => {
      const midAngle = (slice.startAngle + slice.endAngle) / 2;
      return {
        ...slice,
        midAngle,
        baseX: labelDistance * Math.cos(midAngle),
        baseY: labelDistance * Math.sin(midAngle),
        finalX: labelDistance * Math.cos(midAngle),
        finalY: labelDistance * Math.sin(midAngle),
      };
    });

    // Sort positions by Y coordinate for collision detection
    const sortedPositions = [...positions].sort((a, b) => a.baseY - b.baseY);
    
    // Adjust positions to prevent overlaps
    for (let i = 1; i < sortedPositions.length; i++) {
      const current = sortedPositions[i];
      const previous = sortedPositions[i - 1];
      
      if (Math.abs(current.finalY - previous.finalY) < minVerticalSpacing) {
        current.finalY = previous.finalY + minVerticalSpacing;
      }
    }

    return positions;
  };

  const labelPositions = calculateLabelPositions();

  const handleHover = (data: { label: string; value: number; percent: number }) => {
    setHoveredData(data);
  };

  const handleLeave = () => {
    setHoveredData(null);
  };

  return (
    <div className="w-full h-96 relative">
      <Canvas 
        camera={{ position: [200, 150, 200], fov: 45 }}
        shadows
        gl={{ antialias: true }}
      >
        <ambientLight intensity={0.4} />
        <directionalLight 
          position={[10, 10, 5]} 
          intensity={1.5} 
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />
        <directionalLight position={[-10, 10, -5]} intensity={0.8} />
        <pointLight position={[0, 20, 0]} intensity={0.6} />
        
        {/* Fixed angle - no orbit controls for default view */}
        <group rotation={[-Math.PI / 8, Math.PI / 6, 0]}>
          {labelPositions.map((position, index) => (
            <PieSlice
              key={index}
              startAngle={position.startAngle}
              endAngle={position.endAngle}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              thickness={thickness}
              color={position.color}
              label={position.label}
              percent={position.percent}
              value={position.value}
              labelX={position.finalX}
              labelY={position.finalY}
              onHover={handleHover}
              onLeave={handleLeave}
              onSliceClick={onSliceClick}
            />
          ))}
        </group>
      </Canvas>
      
      {/* Hover Tooltip */}
      {hoveredData && (
        <div className="absolute top-4 left-4 bg-card border border-border rounded-lg p-3 shadow-lg z-10">
          <p className="font-medium text-card-foreground">{hoveredData.label}</p>
          <p className="text-sm text-muted-foreground">
            Sites: <span className="font-medium">{hoveredData.value}</span>
          </p>
          <p className="text-sm text-muted-foreground">
            Percentage: <span className="font-medium">{hoveredData.percent.toFixed(1)}%</span>
          </p>
        </div>
      )}
      
      {/* Legend - Horizontal layout at bottom */}
      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-full px-4">
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 max-w-full">
          {data.map((item, index) => (
            <div 
              key={index} 
              className="flex items-center gap-1 text-xs cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => onSliceClick?.(item.label)}
            >
              <div 
                className="w-3 h-3 rounded flex-shrink-0" 
                style={{ backgroundColor: item.color, opacity: 0.8 }}
              />
              <span className="text-foreground whitespace-nowrap">
                {item.label} ({item.value})
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Simple3DPie;